import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Flame, Calendar, MapPin } from "lucide-react";

// Gen-Z horizontal snap-carousel of trending / featured events.
// Fetches from events already loaded in Home — passed as prop to avoid duplicate API calls.
export default function TrendingSlider({ events = [] }) {
  const list = events.filter((e) => {
    if (!(e.trending || e.featured)) return false;
    try {
      const d = e.end_date || e.date;
      if (!d) return true;
      const dt = new Date(d.includes("T") ? d : `${d}T23:59:59`);
      return dt >= new Date();
    } catch { return true; }
  }).slice(0, 12);
  const scrollerRef = useRef(null);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (!list.length) return;
    const timer = setInterval(() => setIdx((i) => (i + 1) % Math.min(list.length, 6)), 5000);
    return () => clearInterval(timer);
  }, [list.length]);

  useEffect(() => {
    const el = scrollerRef.current; if (!el) return;
    const card = el.children[idx]; if (card) el.scrollTo({ left: card.offsetLeft - 16, behavior: "smooth" });
  }, [idx]);

  const scroll = (dir) => {
    const el = scrollerRef.current; if (!el) return;
    el.scrollBy({ left: dir * (el.clientWidth * 0.85), behavior: "smooth" });
  };

  if (!list.length) return null;

  return (
    <section className="relative overflow-hidden py-10" data-testid="trending-slider">
      <div className="blob blob-sm blob-magenta blob-float" style={{ top: "20%", right: "-40px", opacity: 0.25 }} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 mb-4 flex items-end justify-between gap-3">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-magenta/15 text-magenta px-3 py-1 font-mono text-[10px] uppercase tracking-widest">
            <Flame className="w-3.5 h-3.5" /> Trending Now
          </div>
          <h2 className="font-fancy text-xl sm:text-2xl md:text-4xl leading-tight mt-2 text-white">The <span className="grad-text-hot">hottest drops</span> this week.</h2>
        </div>
        <div className="hidden sm:flex gap-2">
          <button onClick={() => scroll(-1)} data-testid="trending-prev" className="icon-circle bg-white/5 hover:bg-white/10"><ChevronLeft className="w-5 h-5" /></button>
          <button onClick={() => scroll(1)} data-testid="trending-next" className="icon-circle bg-lime text-bg hover:scale-105 transition-transform"><ChevronRight className="w-5 h-5" /></button>
        </div>
      </div>
      <div ref={scrollerRef} className="flex gap-4 overflow-x-auto snap-x snap-mandatory px-4 sm:px-6 pb-4 no-scrollbar" style={{ scrollbarWidth: "none" }}>
        {list.map((e, i) => (
          <Link to={`/events/${e.id}`} key={e.id} data-testid={`trending-card-${e.id}`}
            className="snap-start flex-shrink-0 w-[85%] sm:w-[420px] group">
            <div className="relative aspect-[16/9] rounded-3xl overflow-hidden border border-white/10">
              {e.banner ? <img src={e.banner} alt={e.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /> : <div className="w-full h-full bg-gradient-to-br from-magenta/40 to-purplex/40" />}
              <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-transparent" />
              <div className="absolute top-3 left-3 flex gap-2 items-center">
                <span className="px-2.5 py-0.5 rounded-full bg-lime text-ink font-mono text-[9px] uppercase tracking-widest font-bold shadow-glow-lime">#{i + 1} Trending</span>
                {e.category && <span className="px-2.5 py-0.5 rounded-full bg-skyx/90 text-ink font-mono text-[9px] uppercase tracking-widest font-bold">{e.category}</span>}
                {Number(e.going_count) > 0 && <span className="badge-going" data-testid={`trending-going-${e.id}`}>{Number(e.going_count).toLocaleString("en-IN")} going</span>}
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <div className="font-display uppercase text-lg sm:text-xl leading-tight">{e.title}</div>
                <div className="mt-1.5 flex items-center gap-3 text-[10px] font-mono uppercase text-white/70">
                  <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3 text-lime" /> {e?.date ? new Date(e.date).toLocaleDateString("en-IN",{day:"2-digit",month:"short"}) : "TBA"}</span>
                  <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3 text-magenta" /> {e.city || "TBA"}</span>
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 mt-2 flex gap-1 justify-center sm:hidden">
        {list.slice(0, 6).map((_, i) => (
          <div key={i} className={`h-1 rounded-full transition-all ${i === idx % 6 ? "w-6 bg-lime" : "w-1.5 bg-white/20"}`} />
        ))}
      </div>
    </section>
  );
}
